import { defineStore } from 'pinia';

// 定义菜单数据结构
// 菜单项类型
// type: 'menu' - 主菜单（无子菜单）
// type: 'submenu' - 子菜单容器
// type: 'item' - 具体菜单项

export const useMenuStore = defineStore('menu', {
  state: () => ({
    // 原始菜单数据
    rawMenus: [],
    // 处理后的菜单数据（根据权限过滤）
    menus: [],
    // 菜单加载状态
    loading: false,
    // 展开的子菜单列表
    openedSubMenus: [],
    // 当前激活的菜单项
    activeMenuItem: '',
    // 当前激活的一级菜单
    activeFirstMenuItem: '',
  }),

  getters: {
    // 获取当前菜单列表（已过滤权限）
    currentMenus: (state) => state.menus,
    // 判断菜单是否正在加载
    isLoading: (state) => state.loading,
  },

  actions: {
    // 初始化菜单
    async initMenus() {
      try {
        this.loading = true;
        // 从API获取菜单数据
        const menus = await this.fetchMenus();
        this.rawMenus = menus;
        // 根据用户角色过滤菜单
        this.filterMenusByRole();
      } catch (error) {
        console.error('初始化菜单失败:', error);
        // 加载失败时使用空菜单，不使用默认菜单
        this.rawMenus = [];
        this.filterMenusByRole();
      } finally {
        this.loading = false;
      }
    },

    // 从API获取菜单数据
    async fetchMenus() {
      try {
        // 动态导入menuApi，避免循环依赖
        const menuApi = require('../api/menu').default;
        const response = await menuApi.getMenus();
        // 添加详细调试日志，查看菜单数据
        console.log('API响应:', response);
        console.log('菜单数据:', response.data);
        console.log('菜单数据长度:', response.data.length);
        // 检查是否有概况菜单
        const hasDashboard = response.data.some(menu => menu.key === 'dashboard');
        console.log('是否包含概况菜单:', hasDashboard);
        // 打印所有菜单的key
        console.log('所有菜单key:', response.data.map(menu => menu.key));
        // 如果API返回的数据为空，使用空数组，不使用默认菜单
        return response.data || [];
      } catch (error) {
        console.error('获取菜单数据失败:', error);
        // 失败时使用空数组，不使用默认菜单
        return [];
      }
    },

    // 根据用户角色过滤菜单
    filterMenusByRole() {
      // 获取当前用户角色
      const userRole = this.getUserRole();
      
      console.log('当前用户角色:', userRole);
      console.log('过滤前菜单数量:', this.rawMenus.length);
      console.log('过滤前菜单:', this.rawMenus);
      
      // 根据角色过滤菜单
      this.menus = this.filterMenus(this.rawMenus, userRole);
      
      console.log('过滤后菜单数量:', this.menus.length);
      console.log('过滤后菜单:', this.menus);
      // 检查是否包含概况菜单
      const hasDashboard = this.menus.some(menu => menu.key === 'dashboard');
      console.log('过滤后是否包含概况菜单:', hasDashboard);
      // 打印所有过滤后的菜单key
      console.log('过滤后菜单key:', this.menus.map(menu => menu.key));
    },

    // 递归过滤菜单
    filterMenus(menus, role) {
      return menus.filter(menu => {
        // 检查菜单项是否有权限
        if (!this.hasPermission(menu, role)) {
          return false;
        }
        
        // 如果有子菜单，递归过滤子菜单
        if (menu.type === 'submenu' && menu.children) {
          const filteredChildren = this.filterMenus(menu.children, role);
          // 如果子菜单过滤后为空，不显示该父菜单
          if (filteredChildren.length === 0) {
            return false;
          }
          menu.children = filteredChildren;
        }
        
        return true;
      });
    },

    // 检查菜单项是否有权限
    hasPermission(menu, role) {
        try {
            // 获取当前用户信息
            const { useAuthStore } = require('./auth');
            const authStore = useAuthStore();
            
            // 超级管理员拥有所有权限
            if (authStore.isSuperAdmin) {
                return true;
            }
            
            // 如果没有设置roles，默认所有角色都可以访问
            if (!menu.roles || menu.roles.length === 0) {
                return true;
            }
            
            // 检查当前角色是否在允许访问的角色列表中
            return menu.roles.includes(role);
        } catch (error) {
            console.error('检查权限失败:', error);
            // 出错时默认允许访问
            return true;
        }
    },

    // 获取当前用户角色
    getUserRole() {
      // 从auth store获取用户角色
      try {
        // 动态导入auth store，避免循环依赖
        const { useAuthStore } = require('./auth');
        const authStore = useAuthStore();
        // 使用auth store中定义的getter获取用户角色
        return authStore.userRole || 'admin';
      } catch (error) {
        console.error('获取用户角色失败:', error);
        // 失败时返回默认角色
        return 'admin';
      }
    },

    // 设置当前激活的菜单项
    setActiveMenuItem(path) {
      // 保留路径中的前导斜杠，确保与el-menu-item的index属性匹配
      this.activeMenuItem = path;
    },
    
    // 设置当前激活的一级菜单
    setActiveFirstMenuItem(key) {
      this.activeFirstMenuItem = key;
    },

    // 自动展开当前菜单项的父菜单
    autoOpenParentMenus(path) {
      const parentMenuKey = this.findParentMenuKey(this.menus, path);
      if (parentMenuKey && !this.openedSubMenus.includes(parentMenuKey)) {
        this.openedSubMenus.push(parentMenuKey);
      }
    },

    // 查找父菜单的key
    findParentMenuKey(menus, path) {
      for (const menu of menus) {
        if (menu.type === 'submenu' && menu.children) {
          // 检查子菜单中是否包含当前路径
          const hasChild = menu.children.some(child => 
            child.path === path || 
            (child.type === 'submenu' && this.findParentMenuKey(child.children, path))
          );
          if (hasChild) {
            return menu.key;
          }
        }
      }
      return null;
    },

    // 切换子菜单展开/折叠状态
    toggleSubMenu(key) {
      const index = this.openedSubMenus.indexOf(key);
      if (index > -1) {
        this.openedSubMenus.splice(index, 1);
      } else {
        this.openedSubMenus.push(key);
      }
    },

    // 设置子菜单展开状态
    setOpenedSubMenus(keys) {
      this.openedSubMenus = keys;
    },



    // 更新菜单数据
    updateMenus(menus) {
      this.rawMenus = menus;
      this.filterMenusByRole();
    },

    // 重置菜单状态
    reset() {
      this.rawMenus = [];
      this.menus = [];
      this.loading = false;
      this.openedSubMenus = [];
      this.activeMenuItem = '';
      this.activeFirstMenuItem = '';
    }
  },
});
